import React from "react";
// Relative, not "@/...": rendered by screens under component test, where the "@" alias does
// not exist (see components/ui/LoadFailure.jsx).
import { useT } from "../lib/LanguageContext";
import LoadFailure from "../components/ui/LoadFailure";

/**
 * The Super Admin's answer to "you may not see this", and the shared way a screen reports a
 * failed read.
 *
 * A platform operator's role (Finance, Support, Read only) opens some screens and not others.
 * The sidebar hides what the role cannot read and SuperAdminLayout answers a direct visit with
 * `NoAccessPanel`; a screen whose secondary read is refused (a 403 on one panel) shows the same
 * panel in place of that part. A 403 is never an unhandled rejection and never "could not load".
 */

/** Whether a failed request was refused for lack of permission. */
export const isForbidden = (error) => error?.response?.status === 403;

export function NoAccessPanel({ testId = "sa-no-access", className = "" }) {
  const t = useT("superadmin");
  return (
    <div role="alert" data-testid={testId}
      className={`rounded-2xl bg-white border border-slate-200 p-8 text-center ${className}`}>
      <p className="font-semibold text-slate-800">{t("access.deniedTitle")}</p>
      <p className="text-sm text-slate-500 mt-1">{t("access.deniedBody")}</p>
    </div>
  );
}

/**
 * A failed read, as the operator should see it: the no-access panel for a 403, the shared
 * "could not load" with a retry for anything else, nothing when `error` is null.
 *
 * `error`: the rejected request (or any thrown value) | null.
 */
export function PageLoadError({ error, onRetry, testId }) {
  const t = useT("superadmin");
  const tc = useT("common");
  if (!error) return null;
  if (isForbidden(error)) return <NoAccessPanel testId={testId ? `${testId}-forbidden` : undefined} />;
  return (
    <LoadFailure
      message={tc("errors.loadFailed")}
      onRetry={onRetry}
      retryLabel={t("sal.retry")}
      testId={testId}
      retryTestId={testId ? `${testId}-retry` : undefined}
    />
  );
}
